import { createContext, useCallback, useContext, useState } from 'react';
import { Outlet } from 'react-router-dom';
import type { Parameters, ParametersContext } from './ParametersProvider.types';

const Context = createContext<ParametersContext | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useParameters = (): ParametersContext => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useParameters must be used within a ParametersProvider');
  }
  return context;
};

const ParametersProvider = ({ children }: { children?: React.ReactNode }) => {
  const [parameters, _setParameters] = useState<Parameters>({
    search: '',
    sort: '',
    pagination: {
      current: 1,
      pageSize: 20,
    },
    filters: {},
  });
  const [context, setContext] = useState<Record<string, string>>({});

  const setParameter = useCallback((key: keyof Parameters, value: Parameters[keyof Parameters]) => {
    _setParameters((old) => ({
      ...old,
      [key]: value,
    }));
  }, []);

  const setParameters = useCallback((filters: Partial<Parameters>) => {
    _setParameters((old) => ({
      ...old,
      ...filters,
    }));
  }, []);

  return (
    <Context.Provider value={{ parameters, context, setParameter, setParameters, setContext }}>
      {children || <Outlet />}
    </Context.Provider>
  );
};

export default ParametersProvider;
